import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { z } from 'zod';
import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from '@/ui';
import { getErrorMessage } from '@/utils/errors';
import { useEnableExperienceTemplate } from '../hooks/useEnableExperienceTemplate';
import type { TemplateAccountExperience } from '../hooks/useTemplateAccountExperiences';

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  description: z.string().trim(),
});

type FormValues = z.infer<typeof schema>;

type Props = {
  experience: TemplateAccountExperience;
  isOpen: boolean;
  onClose: () => void;
};

// Enables a template, or edits the name and description of one that is
// already enabled; the mutation upserts either way.
export const TemplateModal = ({ experience, isOpen, onClose }: Props) => {
  const { template } = experience;
  const { enableExperienceTemplate, isPending, error, reset } =
    useEnableExperienceTemplate();

  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: template?.name ?? experience.title,
      description: template?.description ?? '',
    },
    resolver: standardSchemaResolver(schema),
  });

  const handleClose = () => {
    if (!isPending) {
      resetForm();
      reset();
      onClose();
    }
  };

  const onSubmit: SubmitHandler<FormValues> = ({ name, description }) => {
    enableExperienceTemplate(
      {
        input: {
          experienceId: experience.id,
          name,
          description: description || null,
        },
      },
      { onSuccess: handleClose }
    );
  };

  return (
    <Modal isCentered isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent maxW="lg">
        <ModalHeader>
          {template ? 'Edit Template' : 'Enable Template'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing="4">
            <FormControl isInvalid={!!errors.name} isRequired>
              <FormLabel>Name</FormLabel>
              <Input {...register('name')} />
              <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!errors.description}>
              <FormLabel>Description</FormLabel>
              <Textarea {...register('description')} />
              <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
            </FormControl>
            {!experience.isPublic && (
              <Text color="gray.600" fontSize="sm">
                Enabling this template also makes the experience public.
              </Text>
            )}
          </VStack>
          {error && (
            <Alert mt="4" status="error">
              <AlertIcon />
              {getErrorMessage(error)}
            </Alert>
          )}
        </ModalBody>
        <ModalFooter gap="3">
          <Button
            isDisabled={isPending}
            variant="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button isLoading={isPending} onClick={handleSubmit(onSubmit)}>
            {template ? 'Save' : 'Enable'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
